import { Injectable, NotFoundException, ConflictException } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model } from 'mongoose';
import { State, StateDocument } from '../schemas/state.schema';
import { Country, CountryDocument } from '../schemas/country.schema';
import { CreateStateDto } from '../dto/create-state.dto';
import { UpdateStateDto } from '../dto/update-state.dto';

@Injectable()
export class StateService {
  constructor(
    @InjectModel(State.name) private stateModel: Model<StateDocument>,
    @InjectModel(Country.name) private countryModel: Model<CountryDocument>,
  ) {}

  async create(createStateDto: CreateStateDto): Promise<State> {
    // Check if country exists
    const country = await this.countryModel.findById(createStateDto.countryId);
    if (!country) {
      throw new NotFoundException(`Country with ID ${createStateDto.countryId} not found`);
    }

    // Check if state with same code already exists in the country
    const existingState = await this.stateModel.findOne({ 
      code: createStateDto.code,
      countryId: createStateDto.countryId
    });
    
    if (existingState) {
      throw new ConflictException(`State with code ${createStateDto.code} already exists in this country`);
    }

    const createdState = new this.stateModel(createStateDto);
    return createdState.save();
  }

  async findAll(): Promise<State[]> {
    return this.stateModel
      .find({ isActive: true })
      .populate('countryId', 'name code')
      .sort({ name: 1 })
      .exec();
  }

  async findByCountry(countryId: string): Promise<State[]> {
    // Check if country exists
    const country = await this.countryModel.findById(countryId);
    if (!country) {
      throw new NotFoundException(`Country with ID ${countryId} not found`);
    }

    return this.stateModel
      .find({ countryId, isActive: true })
      .sort({ name: 1 })
      .exec();
  }

  async findOne(id: string): Promise<State> {
    const state = await this.stateModel
      .findById(id)
      .populate('countryId', 'name code')
      .exec();
    
    if (!state) {
      throw new NotFoundException(`State with ID ${id} not found`);
    }
    
    return state;
  }

  async update(id: string, updateStateDto: UpdateStateDto): Promise<State> {
    // Check if country exists (if countryId is being updated)
    if (updateStateDto.countryId) {
      const country = await this.countryModel.findById(updateStateDto.countryId);
      if (!country) {
        throw new NotFoundException(`Country with ID ${updateStateDto.countryId} not found`);
      }
    }

    // Check if state with same code already exists in the country
    if (updateStateDto.code) {
      const countryId = updateStateDto.countryId;
      const existingState = await this.stateModel.findOne({ 
        code: updateStateDto.code,
        countryId: countryId,
        _id: { $ne: id }
      });
      
      if (existingState) {
        throw new ConflictException(`State with code ${updateStateDto.code} already exists in this country`);
      }
    }

    const updatedState = await this.stateModel
      .findByIdAndUpdate(id, updateStateDto, { new: true })
      .populate('countryId', 'name code')
      .exec();
    
    if (!updatedState) {
      throw new NotFoundException(`State with ID ${id} not found`);
    }
    
    return updatedState;
  }

  async remove(id: string): Promise<void> {
    const result = await this.stateModel.findByIdAndDelete(id).exec();
    if (!result) {
      throw new NotFoundException(`State with ID ${id} not found`);
    }
  }

  async softDelete(id: string): Promise<State> {
    const updatedState = await this.stateModel
      .findByIdAndUpdate(id, { isActive: false }, { new: true })
      .populate('countryId', 'name code')
      .exec();
    
    if (!updatedState) {
      throw new NotFoundException(`State with ID ${id} not found`);
    }
    
    return updatedState;
  }

  async bulkCreate(statesData: CreateStateDto[]): Promise<State[]> {
    const createdStates: State[] = [];
    
    for (const stateData of statesData) {
      // Check if country exists
      const country = await this.countryModel.findById(stateData.countryId);
      if (!country) {
        throw new NotFoundException(`Country with ID ${stateData.countryId} not found`);
      }

      // Check if state with same code already exists in the country
      const existingState = await this.stateModel.findOne({ 
        code: stateData.code,
        countryId: stateData.countryId
      });
      
      if (!existingState) {
        const createdState = new this.stateModel(stateData);
        const savedState = await createdState.save();
        createdStates.push(savedState);
      }
    }
    
    return createdStates;
  }
}